/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { FormattedMessage } from 'react-intl';
import { Box } from 'src/components/Box';
import { Card } from 'src/components/Card';
import { CollapseContent } from 'src/components/CollapseContent';
import { Container } from 'src/components/Container';
import { Loading } from 'src/components/Loading';
import { ServerInfoDash } from 'src/components/ServerInfoDash/ServerInfoDash';
import { SquarePill } from 'src/components/SquarePill/SquarePill';
import { H1, H6, Span, Text } from 'src/components/Text';
import { useSubscriptions } from 'src/hooks/useSubscriptions/useSubscriptions';
import { messages } from 'src/messages';
import { style } from './SubscriptionServers.style';
import { ServerStatusIcon } from 'src/components/ServerStatusIcon/ServerStatusIcon';
import {
  MysqlKind,
  NetworkStatus,
  ServerRole,
  Status,
  Subscription,
  SubscriptionDedicatedServer,
  WebserverKind,
} from 'src/orchd-client';
import { Copy } from 'src/components/Copy/Copy';
import { DisplayRole } from 'src/components/PackageSubscriptionCard/DisplayPackageServers';
import { useEffect, useState } from 'react';
import { serversApi } from 'src/api_services/servers/service';
import { IconName } from 'src/components/Icon/Icon.types';

interface Props {
  orgId: string;
}

export const SubscriptionServers = ({ orgId }: Props) => {
  const { isLoading, subscriptions } = useSubscriptions({ orgId });

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Container pb="4xl">
      <Box j="space-between" a="center" mb="2xl" h="48px">
        <Box d="column" pr="xl" w={{ xs: 12, sm: 6 }}>
          <H1 size="3xl">
            <FormattedMessage {...messages.my_servers} />
          </H1>
        </Box>
      </Box>
      <Box>
        {subscriptions
          .filter((sub) => sub.dedicatedServers)
          .filter((sub) => sub.status !== Status.deleted)
          .map((sub) => {
            const arr = [
              sub.dedicatedServers?.appServer,
              sub.dedicatedServers?.dbServer,
              sub.dedicatedServers?.emailServer,
              sub.dedicatedServers?.backupServer,
            ].filter(Boolean);
            const deduped = [...new Map(arr.map((item) => [item.id, item])).values()];

            return (
              <CollapseContent
                role="group"
                contentMarginTop="sm"
                enableShowHideText={false}
                defaultCollapsed={false}
                label={
                  <Box a="center" wrap="nowrap">
                    <H6 size="sm" transform="uppercase" weight="normal">
                      <span style={{ marginRight: '8px' }}>{sub.planName}</span>
                      {'  '}
                      {sub.friendlyName && sub.friendlyName !== sub.planName && (
                        <SquarePill size="sm" color="accent" text={sub.friendlyName} />
                      )}
                    </H6>
                  </Box>
                }
                marginBottom="2xl"
                labelLocation="left"
              >
                {deduped.map((server) => (
                  <SubscriptionServerCard server={server} sub={sub} />
                ))}
              </CollapseContent>
            );
          })}
      </Box>
    </Container>
  );
};

export const webserverIcons: Record<WebserverKind, IconName> = {
  [WebserverKind.apache]: 'apache',
  [WebserverKind.liteSpeed]: 'litespeed',
  [WebserverKind.openLiteSpeed]: 'openlitespeed',
  [WebserverKind.nginx]: 'nginx',
  [WebserverKind.dummyWebServer]: 'server',
};

export const mysqlKindIcons: Record<MysqlKind, IconName> = {
  [MysqlKind.mariaDb11]: 'mariadb',
  [MysqlKind.mariaDbLts]: 'mariadb',
  [MysqlKind.mysql80]: 'mysql',
  [MysqlKind.mysql81]: 'mysql',
};

interface SubscriptionServerCardProps {
  server: SubscriptionDedicatedServer;
  sub: Subscription;
}

const SubscriptionServerCard = ({ server, sub }: SubscriptionServerCardProps) => {
  const [webServerKind, setWebServerKind] = useState<WebserverKind>();
  const [mysqlKind, setMysqlKind] = useState<MysqlKind>();

  useEffect(() => {
    serversApi
      .getServerRoles({
        serverId: server.id,
      })
      .then((roles) => {
        roles.data.mysqlKind && setMysqlKind(roles.data.mysqlKind);
        roles.data.webserverKind && setWebServerKind(roles.data.webserverKind);
      });
  }, [server]);
  return (
    <Box mb="sm">
      <Card
        css={style.container()}
        data-qa="server-overview-card"
        paddingLeft="3xl"
        fullWidth
        fullHeight
        marginBottom="xl"
        minWidth="0"
      >
        <ServerStatusIcon
          data-qa="server-status-icon"
          isUpdating={false}
          isDecommissioned={false}
          css={style.serverStatusIcon}
          status={NetworkStatus.online}
        />
        <Text marginRight="sm" weight="light" size="xl" ellipsis>
          {server.name}
        </Text>

        <Box wrap="nowrap" mb="sm">
          <Copy
            value={server.ip}
            label={
              <Span size="sm" color="grey" ellipsis fullWidth mono>
                {server.ip}
              </Span>
            }
          />
        </Box>

        <Box d="row" mb="sm" g="xs">
          {sub.dedicatedServers?.appServer && sub.dedicatedServers?.appServer?.id === server.id && (
            <Box maxW={{ xs: '100%', md: '140px' }} a="center">
              <DisplayRole
                roleKind={ServerRole.application}
                serverId={undefined}
                icon={webServerKind ? webserverIcons[webServerKind] : 'server'}
                isMasterOrg={false}
                isDisabled={false}
              />
            </Box>
          )}
          {sub.dedicatedServers?.dbServer && sub.dedicatedServers?.dbServer?.id === server.id && (
            <Box maxW={{ xs: '100%', md: '140px' }} a="center">
              <DisplayRole
                roleKind={ServerRole.database}
                serverId={undefined}
                icon={mysqlKind ? mysqlKindIcons[mysqlKind] : 'server'}
                isMasterOrg={false}
                isDisabled={false}
              />
            </Box>
          )}
          {sub.dedicatedServers?.emailServer && sub.dedicatedServers?.emailServer.id === server.id && (
            <Box maxW={{ xs: '100%', md: '140px' }} a="center">
              <DisplayRole
                roleKind={ServerRole.email}
                serverId={undefined}
                icon="mail_envelope"
                isMasterOrg={false}
                isDisabled={false}
              />
            </Box>
          )}
          {sub.dedicatedServers?.backupServer && sub.dedicatedServers?.backupServer.id === server.id && (
            <Box maxW={{ xs: '100%', md: '140px' }} a="center">
              <DisplayRole
                roleKind={ServerRole.backup}
                serverId={undefined}
                icon="cloud_upload"
                isMasterOrg={false}
                isDisabled={false}
              />
            </Box>
          )}
        </Box>

        <Box bg="grey5" mt="md" mb="xl" p="xl">
          <ServerInfoDash serverId={server.id} />
        </Box>
      </Card>
    </Box>
  );
};
